// Trip card components & shared utilities used by the Trips module.
import React from 'react';
import { CalendarBlank, MapPin, Users, CurrencyInr, Sparkle, Airplane, CheckCircle, Clock } from '@phosphor-icons/react';

export const TRIP_STATUS = [
  { id: 'planned',   label: 'Planned',   color: '#3B82F6', tint: 'rgba(59,130,246,0.15)' },
  { id: 'active',    label: 'On trip',   color: '#22C55E', tint: 'rgba(34,197,94,0.15)' },
  { id: 'completed', label: 'Completed', color: '#A3A3A3', tint: 'rgba(163,163,163,0.15)' },
  { id: 'cancelled', label: 'Cancelled', color: '#EF4444', tint: 'rgba(239,68,68,0.10)' },
];

export const ITINERARY_CATEGORIES = [
  { id: 'lodging',   label: 'Lodging',   emoji: '🏨' },
  { id: 'food',      label: 'Food',      emoji: '🍽️' },
  { id: 'transport', label: 'Transport', emoji: '🚌' },
  { id: 'activity',  label: 'Activity',  emoji: '🎯' },
  { id: 'sight',     label: 'Sight',     emoji: '📸' },
];

export const BUDGET_CATEGORIES = [
  { id: 'transport',  label: 'Transport',  color: '#3B82F6' },
  { id: 'lodging',    label: 'Lodging',    color: '#A855F7' },
  { id: 'food',       label: 'Food',       color: '#F59E0B' },
  { id: 'activities', label: 'Activities', color: '#10B981' },
  { id: 'shopping',   label: 'Shopping',   color: '#EC4899' },
  { id: 'misc',       label: 'Misc',       color: '#64748B' },
];

export const PACKING_CATEGORIES = [
  { id: 'clothes',     label: 'Clothes',     emoji: '👕' },
  { id: 'toiletries',  label: 'Toiletries',  emoji: '🧴' },
  { id: 'electronics', label: 'Electronics', emoji: '🔌' },
  { id: 'docs',        label: 'Documents',   emoji: '📄' },
  { id: 'medical',     label: 'Medical',     emoji: '💊' },
  { id: 'general',     label: 'General',     emoji: '🎒' },
];

export const CURRENCY_SYMBOL = { INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'د.إ', SGD: 'S$' };

export function fmtMoney(amount, currency = 'INR') {
  const sym = CURRENCY_SYMBOL[currency] || (currency + ' ');
  try {
    return `${sym}${Number(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  } catch {
    return `${sym}${amount}`;
  }
}

export function tripDays(t) {
  try {
    if (!t?.start_date || !t?.end_date) return 1;
    const a = new Date(t.start_date + 'T00:00');
    const b = new Date(t.end_date + 'T00:00');
    return Math.max(1, Math.round((b - a) / 86400000) + 1);
  } catch { return 1; }
}

export function daysUntil(dateStr) {
  if (!dateStr) return null;
  try {
    const a = new Date(); a.setHours(0, 0, 0, 0);
    const b = new Date(dateStr + 'T00:00');
    return Math.round((b - a) / 86400000);
  } catch { return null; }
}

export function StatusChip({ status }) {
  const s = TRIP_STATUS.find(x => x.id === status) || TRIP_STATUS[0];
  return (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{ background: s.tint, color: s.color, border: `1px solid ${s.color}40` }}>
      {s.label}
    </span>
  );
}

export function TripCard({ trip, onOpen }) {
  const days = tripDays(trip);
  const until = daysUntil(trip.start_date);
  const cur = trip.currency || 'INR';
  return (
    <button onClick={onOpen} data-testid={`trip-card-${trip.trip_id}`}
      className="text-left rounded-2xl p-4 sm:p-5 transition-all hover:scale-[1.01] active:scale-[0.99] group"
      style={{
        background: trip.travel_mode_active
          ? 'linear-gradient(135deg, rgba(34,197,94,0.10), rgba(34,197,94,0.02))'
          : 'rgba(255,255,255,0.03)',
        border: `1px solid ${trip.travel_mode_active ? 'rgba(34,197,94,0.40)' : 'rgba(255,255,255,0.08)'}`,
      }}>
      <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-base sm:text-lg font-bold text-white truncate">{trip.title}</h3>
          {trip.travel_mode_active && (
            <span className="px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-widest flex items-center gap-1 flex-shrink-0"
              style={{ background: 'rgba(34,197,94,0.20)', color: '#86EFAC', border: '1px solid rgba(34,197,94,0.50)' }}>
              <Airplane size={9} weight="fill" /> On trip
            </span>
          )}
        </div>
        <StatusChip status={trip.status} />
      </div>

      <div className="flex items-center gap-1.5 text-[12px] text-neutral-400 mb-2">
        <MapPin size={12} weight="fill" className="text-[#FF6B35]" />
        <span className="truncate">{trip.destination || 'No destination'}</span>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-3">
        <div className="rounded-lg p-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-[9px] uppercase tracking-wider text-neutral-500 flex items-center gap-1"><CalendarBlank size={9} /> Days</p>
          <p className="text-sm font-bold text-white mt-0.5">{days}</p>
        </div>
        <div className="rounded-lg p-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-[9px] uppercase tracking-wider text-neutral-500 flex items-center gap-1"><Users size={9} /> Party</p>
          <p className="text-sm font-bold text-white mt-0.5">{trip.party_size || 1}</p>
        </div>
        <div className="rounded-lg p-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-[9px] uppercase tracking-wider text-neutral-500 flex items-center gap-1"><CurrencyInr size={9} /> Budget</p>
          <p className="text-sm font-bold text-white mt-0.5">{fmtMoney(trip.budget_amount, cur)}</p>
        </div>
      </div>

      {trip.start_date && (
        <div className="mt-3 flex items-center justify-between gap-2 text-[10px] text-neutral-500">
          <span className="flex items-center gap-1"><Clock size={10} /> {trip.start_date} → {trip.end_date || '?'}</span>
          {until !== null && trip.status === 'planned' && (
            <span className={`font-bold ${until < 0 ? 'text-red-400' : until === 0 ? 'text-emerald-400' : 'text-amber-400'}`}>
              {until < 0 ? `${Math.abs(until)}d past` : until === 0 ? 'Today!' : `${until}d to go`}
            </span>
          )}
          {trip.status === 'completed' && (
            <span className="flex items-center gap-1 text-emerald-400 font-bold"><CheckCircle size={10} weight="fill" /> Done</span>
          )}
        </div>
      )}
    </button>
  );
}

export function EmptyTripsState({ onCreate }) {
  return (
    <div className="rounded-2xl p-10 text-center"
      style={{ background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.10)' }}
      data-testid="trips-empty-state">
      <div className="text-5xl mb-3">✈️</div>
      <h3 className="text-base font-bold text-white mb-1">No trips yet</h3>
      <p className="text-sm text-neutral-500 mb-4">Plan your next getaway — AI will draft the itinerary, budget &amp; packing list for you.</p>
      <button onClick={onCreate} data-testid="trips-empty-create-btn"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#FF6B35] text-white text-sm font-bold hover:bg-[#E85D2A]">
        <Sparkle size={14} weight="fill" /> Plan your first trip
      </button>
    </div>
  );
}
